import type { QueryClient } from '@tanstack/react-query';
import { Capacitor } from '@capacitor/core';
import { apiClient } from '@/services/api';
import { analyticsService } from '@/services/analytics';
import { useAppStore } from '@/store/appStore';
import { clearUserData } from '@/utils/userStorage';
import { NativeLoginPlugin } from '@/plugins/nativeLogin';

/**
 * Forget the account on this device and start signing in from scratch: the
 * token, every cached query (the on-disk copy too), the user-scoped storage,
 * the store and the analytics identity. Then iOS opens its native sheet;
 * Android and the web reload, and a launch with no token lands on the login
 * page (Auth.tsx).
 *
 * After logout and account deletion, and when the server no longer accepts
 * the session (the account was deleted elsewhere): the cached profile would
 * otherwise keep the app open with every request failing.
 *
 * `webReturnTo`: where the web lands instead of `/` (useLogout).
 */
export async function restartSignIn(queryClient: QueryClient, webReturnTo?: string): Promise<void> {
    await apiClient.clearToken();
    queryClient.clear();
    await clearUserData();
    useAppStore.getState().reset();
    analyticsService.reset();

    if (!Capacitor.isNativePlatform()) {
        window.location.href = webReturnTo || '/';
        return;
    }
    // Only iOS has the native sheet.
    if (Capacitor.getPlatform() !== 'ios') {
        window.location.reload();
        return;
    }
    try {
        const result = await NativeLoginPlugin.showLogin();
        if (result.success) {
            await apiClient.loadToken(true);
            window.location.reload();
        }
    } catch {
        window.location.reload();
    }
}
